import type { AxePurchaseState, v2 } from '@deque/billing-service-client';
import { getControllingProductSubscription } from '@deque/billing-utils';
import {
  Alert,
  AlertActions,
  AlertContent,
  Button
} from '@deque/cauldron-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CreateApiKeyBody, UpdateApiKeyBody } from '~/common/api-client';
import ContentToast from '~/common/components/ContentToast';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import { ACTIVE_STATES, ProductSlugs } from '~/common/constants';
import { useAuthContext } from '~/common/contexts/auth';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import { useProducts } from '~/common/contexts/products';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import { dayjs } from '~/common/utils/dates';
import AddEditApiKey, {
  type EditParams,
  type ValidateNameCallback
} from '../components/AddEditApiKey';
import ApiKeyManagementComponent from '../components/ApiKeyManagement';
import type { ApiKeyTableData } from '../components/ApiKeyTable';
import useApiKey from '../hooks/useApiKey';

const ApiKeyManagement: React.FC = () => {
  const { t } = useTranslation();
  const wide = useMediaQuery('(min-width: 49rem)');
  const narrow = useMediaQuery('(max-width: 31rem)');
  const variant = wide ? 'wide' : narrow ? 'narrow' : 'medium';
  const { user, billingUser } = useAuthContext();
  // There will always be a user and with a token.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = user!.token;
  const {
    products,
    loading: loadingProducts,
    error: productsError
  } = useProducts();
  /* istanbul ignore next */
  const {
    apiKeys,
    error: apiKeyError,
    loading: loadingApiKeys,
    list,
    create,
    deleteById,
    updateById,
    regenerateApiKey
  } = useApiKey(token, true);
  const hasAxeMCPServerV1 = useFeatureFlagState('axe_mcp_server_v1');
  const hasFreemiumAxeMCPServerV1 = useFeatureFlagState(
    'freemium_axe_mcp_server_v1'
  );

  const [confirmation, setConfirmation] = useState('');
  const [apiKeyErr, setApiKeyErr] = useState('');
  const [isApiCall, setIsApiCall] = useState(true);
  const [confirmationMsg, setConfirmationMsg] = useState<string | null>(null);
  const [showApiKeyError, setShowApiKeyError] = useState(false);
  const [apiKeyErrMsg, setApiKeyErrMsg] = useState('');
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [isAddEdit, setIsAddEdit] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editParams, setEditParams] = useState<EditParams | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const [alertType, setAlertType] = useState<'delete' | 'regenerate'>();
  const [alertBtnText, setAlertBtnText] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const [focusReturn, setFocusReturn] = useState<Element | null>(null);

  const allowsApiKeys = useCallback(
    (p: v2.Product): boolean => {
      if (p.api_key_generation !== 'internal') {
        return false;
      }

      // Should be unreachable, we expect not to render this container unless auth'd
      /* istanbul ignore next */
      if (!billingUser) {
        return false;
      }

      const slug = p.slug as ProductSlugs;
      if (!hasAxeMCPServerV1 && slug === ProductSlugs.axeMCPServer) {
        return false;
      }

      const subscription = getControllingProductSubscription({
        entity: billingUser,
        productSlug: slug,
        includeEnterprises: 'active-only'
      });

      if (!subscription) {
        return false;
      }

      const purchase_state = subscription.purchase_state as AxePurchaseState;

      if (ACTIVE_STATES.includes(purchase_state)) {
        return true;
      }

      // A free-tier axe-devtools-html subscription also entitles MCP API keys.
      if (
        slug === ProductSlugs.axeMCPServer &&
        hasFreemiumAxeMCPServerV1 &&
        purchase_state === 'free'
      ) {
        return true;
      }

      return false;
    },
    [billingUser, hasAxeMCPServerV1, hasFreemiumAxeMCPServerV1]
  );

  // only display products with internal API key and a valid subscription from the user
  const apiKeyProducts: v2.Product[] = useMemo(() => {
    return products.filter(allowsApiKeys);
  }, [products, allowsApiKeys]);

  // Not displaying axe-auditor-integration keys per #10095 ticket.
  const filteredApiKeys = useMemo(() => {
    return apiKeys.filter(apiKey => {
      return apiKey.product_slug !== ProductSlugs.axeAuditorIntegration;
    });
  }, [apiKeys]);

  const apiKeyTableData: ApiKeyTableData[] = useMemo(() => {
    return filteredApiKeys.map(apiKey => {
      return {
        id: apiKey.id,
        api_key: apiKey.api_key,
        name: apiKey.name,
        product_name:
          /* istanbul ignore next */
          apiKeyProducts.find(product => product.slug === apiKey.product_slug)
            ?.name || '',
        product_slug: apiKey.product_slug,
        elapsed_time: dayjs(apiKey.updated_at || apiKey.created_at).fromNow(),
        tags: []
      };
    });
  }, [filteredApiKeys, apiKeyProducts]);

  useEffect(() => {
    if (confirmation) {
      handleConfirmation(confirmation);
    }
  }, [confirmation]);

  useEffect(() => {
    if (apiKeyErr) {
      handleApiKeyErrorMsg(apiKeyErr);
    }
  }, [apiKeyErr]);

  useEffect(() => {
    list();
    setApiKeyErr(t('load'));
  }, []);

  useEffect(() => {
    if (!isAddEdit) {
      return;
    }

    setIsApiCall(true);
    if (apiKeyError?.status && apiKeyError.status >= 500) {
      setApiKeyErr(isEditMode ? t('edit') : t('add'));
    }
    setConfirmation(isEditMode ? t('edited') : t('added'));

    if (isAddEdit) {
      setIsAddEdit(false);
      setShowApiKeyModal(false);
    }
  }, [loadingApiKeys, apiKeyError, isAddEdit]);

  useEffect(() => {
    if (!showApiKeyModal && !showAlert && !loadingApiKeys && focusReturn) {
      (focusReturn as HTMLElement).focus();
      setFocusReturn(null);
    }
  }, [showApiKeyModal, showAlert, loadingApiKeys]);

  const handleConfirmation = (operation: string) => {
    if (!loadingApiKeys && !apiKeyError) {
      setConfirmationMsg(
        t('API key was successfully {{ operation }}', { operation })
      );
    }
  };

  const handleApiKeyErrorMsg = (operation = t('load')) => {
    if (!isApiCall || (apiKeyError && isApiCall)) {
      setShowApiKeyError(true);
      setApiKeyErrMsg(t('Failed to {{ operation }} API Key', { operation }));
    }
  };

  const handleAddApiKey = async (params: CreateApiKeyBody) => {
    // wait until api calls are done
    await create({
      name: params.name,
      product_slug: params.product_slug,
      tags: []
    });
    setIsAddEdit(true);
  };

  const handleEditApiProject = async (updates: UpdateApiKeyBody) => {
    /* istanbul ignore next */
    await updateById(editParams?.id || '', {
      name: updates.name
    });
    setIsAddEdit(true);
  };

  const handleRecreateApiKey = async (id: string) => {
    setShowAlert(false);
    setAlertType(undefined);
    setConfirmationMsg(null);
    setConfirmation('');
    await regenerateApiKey(id);
    setIsApiCall(true);
    setConfirmation(t('regenerated'));
    setApiKeyErr(t('regenerate'));
  };

  const handleDeleteApiKey = async (id: string) => {
    setShowAlert(false);
    setAlertType(undefined);
    setConfirmationMsg(null);
    setConfirmation('');
    await deleteById(id);
    setIsApiCall(true);
    setConfirmation(t('deleted'));
    setApiKeyErr(t('delete'));
  };

  const handleCopyApiKey = async (apiKey: string) => {
    setIsApiCall(false);
    try {
      await navigator.clipboard.writeText(apiKey);
      setConfirmation(t('copied'));
    } catch {
      setApiKeyErr(t('copy'));
    }
  };

  const onAddClick = (): void => {
    setShowApiKeyModal(true);
    setIsEditMode(false);
  };

  const onEditSelect = (
    id: string,
    product_slug: string,
    name: string
  ): void => {
    /* istanbul ignore next */
    setFocusReturn(
      document.activeElement?.parentElement?.previousElementSibling || null
    );
    setShowApiKeyModal(true);
    setIsEditMode(true);
    setEditParams({
      id,
      product_slug,
      name
    });
  };

  const onRegenerateClick = (id: string): void => {
    /* istanbul ignore next */
    setFocusReturn(
      document.activeElement?.parentElement?.previousElementSibling || null
    );
    setApiKeyId(id);
    setAlertMsg(
      t(
        'Regenerating your API key will delete your current API key and create a new one. You will need to replace any instances of your old key in your code with the new one.'
      )
    );
    setAlertType('regenerate');
    setAlertBtnText(t('Regenerate'));
    setShowAlert(true);
  };

  const onDeleteClick = (id: string): void => {
    const onDeleteMsg = t(
      'Deleting your API key will remove your API key. After deletion, any instances of the deleted API in your code will no longer be functional.'
    );
    /* istanbul ignore next */
    setFocusReturn(
      document.activeElement?.parentElement?.previousElementSibling || null
    );
    setApiKeyId(id);
    setAlertMsg(onDeleteMsg);
    setAlertType('delete');
    setAlertBtnText(t('Delete'));
    setShowAlert(true);
  };

  const handleClose = () => {
    setShowApiKeyModal(false);
    if (apiKeyError) {
      // set error to null if a request is aborted due to an error
      list();
    }
  };

  const generateSelectOptions = () => {
    return apiKeyProducts.map(product => {
      return {
        key: product.id,
        value: product.slug,
        label: product.name
      };
    });
  };

  const validateApiKeyName: ValidateNameCallback = name => {
    const isEmptyName = !name.trim();

    if (isEmptyName) {
      return t('Name required');
    }

    // The name must be a maximum of 512 characters because the column `name` in the table `api_keys`
    // has the type "varchar(512)".
    // The migration file: migrations/2021-10-21-add-api-keys-table.sql
    if (name.length > 512) {
      return t('Name must be a maximum of 512 characters');
    }

    const isDuplicateName = !!apiKeys.find(a => a.name === name);

    if (isDuplicateName) {
      return t('API Key name already exists');
    }

    return null;
  };

  const showLoader = loadingApiKeys || loadingProducts;

  return (
    <div>
      {showAlert && (
        <Alert
          heading={
            alertType === 'delete'
              ? t('Delete API key')
              : t('Regenerate API key')
          }
          onClose={() => setShowAlert(false)}
          show={showAlert}
          variant="warning"
        >
          <AlertContent>{alertMsg}</AlertContent>
          <AlertActions>
            <Button
              variant="error"
              onClick={() =>
                alertType === 'delete'
                  ? handleDeleteApiKey(apiKeyId)
                  : handleRecreateApiKey(apiKeyId)
              }
            >
              {alertBtnText}
            </Button>
            <Button variant="secondary" onClick={() => setShowAlert(false)}>
              {t('Cancel')}
            </Button>
          </AlertActions>
        </Alert>
      )}
      {showLoader && !showApiKeyModal && (
        <ScrimmedLoader label={t('Loading...')} />
      )}
      {confirmationMsg && (
        <ContentToast
          type="confirmation"
          show
          onDismiss={() => {
            setConfirmationMsg(null);
          }}
        >
          {confirmationMsg}
        </ContentToast>
      )}
      {!!productsError && (
        <ContentToast type="caution" show>
          {t('Failed to load Products')}
        </ContentToast>
      )}
      {showApiKeyError && (
        <ContentToast type="caution" show>
          {apiKeyErrMsg}
        </ContentToast>
      )}
      {showApiKeyModal && (
        <AddEditApiKey
          handleSaveAdd={handleAddApiKey}
          handleSaveEdit={handleEditApiProject}
          handleClose={handleClose}
          showApiKeyModal={showApiKeyModal}
          validateName={validateApiKeyName}
          selectionOptions={generateSelectOptions()}
          isEditMode={isEditMode}
          editParams={isEditMode ? editParams : null}
          error={apiKeyError}
          showLoader={showLoader}
        />
      )}
      <ApiKeyManagementComponent
        apiKeys={apiKeyTableData}
        hasProducts={!!apiKeyProducts.length}
        handleCopyApiKey={handleCopyApiKey}
        onRegenerateClick={onRegenerateClick}
        onDeleteClick={onDeleteClick}
        onAddClick={onAddClick}
        onEditSelect={onEditSelect}
        variant={variant}
      />
    </div>
  );
};

export default ApiKeyManagement;
